import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Chatbox, Session } from 'talkjs/all';
import { Box } from '@rsos/components/capstone/base';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import { PreconfiguredMessageInterface } from '@rsos/reduxDataStore/src/features/alerts/chat.types';
import { setConversationUnread } from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import {
  useGetConversationID,
  useGetConversationSource,
  useHasUnreadConversationMessages,
  useIsConversationEnded,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { createTalkSession, createTalkUser } from '@rsos/utils/talkJS';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import { PRECONFIG_REQUEST_LOCATION } from '../../../PreconfiguredMessages/AllMessages/utils';
import SuggestedResponses from '../../../PreconfiguredMessages/SuggestedResponses';
import RequestLocation from '../../../RequestLocation';
import { useChatContext } from '../ChatContext';
import { OutgoingTranslationWarning } from '../SharedChatUI/OutgoingTranslationWarning';
import { ChatContainer } from './ConversationsChatbox.styles';

interface ConversationsChatProps {
  setIsAllMessagesVisible: (value: boolean) => void;
  selectedPreconfiguredMessage: PreconfiguredMessageInterface | null;
  setSelectedPreconfiguredMessage: (
    value: PreconfiguredMessageInterface | null,
  ) => void;
  hasRequestLocationAccess: boolean;
  preconfiguredMessages: PreconfiguredMessageInterface[];
}

const ConversationsChat = ({
  setIsAllMessagesVisible,
  selectedPreconfiguredMessage,
  setSelectedPreconfiguredMessage,
  hasRequestLocationAccess,
  preconfiguredMessages,
}: ConversationsChatProps) => {
  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );
  const source = useGetConversationSource(callerID);
  const dispatch = useDispatch();
  const chatRef = useRef<HTMLDivElement>(null);
  const [chatSession, setChatSession] = useState<Session | null>(null);
  const [chatbox, setChatbox] = useState<Chatbox | null>(null);
  const chatTheme = useTalkJSTheme(source === SOURCE_TCAPI ? 'tcapi' : 'tf911');
  const conversationID = useGetConversationID(callerID);
  const isConversationEnded = useIsConversationEnded(callerID);
  const loggedInUser = useTF911LoggedInUser();
  const hasUnreadConversationMessages =
    useHasUnreadConversationMessages(callerID);
  const { isCollapsed } = useChatContext();

  const hasSuggestedResponses =
    !isConversationEnded && !isCollapsed && conversationID;

  const handleViewClick = () => {
    setIsAllMessagesVisible(true);
  };

  const handleRequestLocation = (url: string) => {
    if (chatbox && !isConversationEnded && preconfiguredMessages) {
      const locationMessage = preconfiguredMessages.find(
        message => message.title === PRECONFIG_REQUEST_LOCATION,
      );

      const talkjsMessage = locationMessage?.body.replace(/\{\{.*?\}\}/g, url);

      if (talkjsMessage) {
        chatbox.messageField.setText(talkjsMessage);
      }
    }
  };

  // Mount the TalkJS session and chat container
  useEffect(() => {
    let session, chat;
    try {
      (async () => {
        session = await createTalkSession(loggedInUser);

        chat = session.createChatbox({
          theme: chatTheme,
          showChatHeader: false,
          messageField: {
            visible: true,
            placeholder: 'Type Message',
          },
        });

        chat.mount(chatRef.current);

        setChatSession(session);

        setChatbox(chat);
      })();
    } catch (error) {
      if (session) {
        setChatbox(null);
      }
    }
  }, [chatTheme, loggedInUser]);

  // Set Conversation
  useEffect(() => {
    if (chatbox && chatSession && conversationID) {
      const conversation = chatSession.getOrCreateConversation(conversationID);

      conversation.setParticipant(createTalkUser(loggedInUser));
      chatbox.select(conversation);

      chatbox.messageField.setVisible(!isConversationEnded);
    }
  }, [
    chatSession,
    chatbox,
    conversationID,
    isConversationEnded,
    loggedInUser,
    callerID,
  ]);

  // Mark chat as read
  useEffect(() => {
    if (hasUnreadConversationMessages) {
      dispatch(setConversationUnread({ callerID, isUnread: false }));
    }
  }, [hasUnreadConversationMessages, callerID, dispatch]);

  // Set messageField text
  useEffect(() => {
    if (chatbox && !isConversationEnded) {
      if (selectedPreconfiguredMessage) {
        chatbox.messageField.setText(selectedPreconfiguredMessage.body);
        setSelectedPreconfiguredMessage(null);
      }
    }
  }, [
    selectedPreconfiguredMessage,
    chatbox,
    isConversationEnded,
    setSelectedPreconfiguredMessage,
    dispatch,
  ]);

  return (
    <>
      <ChatContainer ref={chatRef} display={isCollapsed ? 'none' : 'flex'} />
      {!isConversationEnded && <OutgoingTranslationWarning />}
      {hasSuggestedResponses && (
        <Box width="100%" horizontal align="center">
          {hasRequestLocationAccess && (
            <RequestLocation onSuccess={handleRequestLocation} />
          )}
          <SuggestedResponses handleViewClick={handleViewClick} />
        </Box>
      )}
    </>
  );
};

export default ConversationsChat;
